import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ExternalLink, ShoppingBag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { StoreProduct } from "@/knowmeno/store/storeQueries";
import { AffiliateDisclosure } from "@/knowmeno/store/AffiliateDisclosure";
import { logStoreClick } from "@/lib/store.functions";

const PRODUCT_COLS =
  "id, slug, category_id, title, brand, short_desc, long_desc, image_url, price_display, affiliate_url, affiliate_network, menopause_relevance, is_active, sort_order";

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1).trimEnd() + "…";
}

export const Route = createFileRoute("/store/$slug")({
  loader: async ({ params }) => {
    const { data, error } = await supabase
      .from("store_products")
      .select(PRODUCT_COLS)
      .eq("slug", params.slug)
      .eq("is_active", true)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw notFound();
    return { product: data as StoreProduct };
  },
  head: ({ params, loaderData }) => {
    const url = `https://knowmeno.com/store/${params.slug}`;
    const product = loaderData?.product;
    if (!product) {
      return {
        meta: [
          { title: "Product — KnowMeno Store" },
          {
            name: "description",
            content: "A KnowMeno-curated pick for women in perimenopause and menopause.",
          },
          { property: "og:url", content: url },
          { name: "robots", content: "index, follow" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }

    const titleBase = product.brand ? `${product.title} — ${product.brand}` : product.title;
    const fullTitle = `${titleBase} | KnowMeno Store`;
    const description = truncate(product.long_desc ?? product.short_desc, 160);

    const meta: Array<Record<string, string>> = [
      { title: fullTitle },
      { name: "description", content: description },
      { name: "robots", content: "index, follow" },
      { property: "og:type", content: "product" },
      { property: "og:title", content: titleBase },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:site_name", content: "KnowMeno" },
      { name: "twitter:card", content: product.image_url ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: titleBase },
      { name: "twitter:description", content: description },
    ];
    if (product.image_url) {
      meta.push({ property: "og:image", content: product.image_url });
      meta.push({ name: "twitter:image", content: product.image_url });
    }

    const productSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.title,
      description: product.long_desc ?? product.short_desc,
      url,
      sku: product.slug,
      offers: {
        "@type": "Offer",
        url: product.affiliate_url,
        availability: "https://schema.org/InStock",
        priceCurrency: "USD",
        ...(product.price_display ? { price: product.price_display.replace(/[^0-9.]/g, "") || "0" } : { price: "0" }),
      },
    };
    if (product.image_url) productSchema.image = product.image_url;
    if (product.brand) productSchema.brand = { "@type": "Brand", name: product.brand };


    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(productSchema),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-xl p-8 text-center">
      <h1 className="text-2xl font-semibold">Product not found</h1>
      <Link to="/store" className="mt-4 inline-block text-primary underline">
        Back to store
      </Link>
    </div>
  ),
  component: ProductPage,
});

function networkLabel(network: string): string {
  switch (network) {
    case "amazon":
      return "Amazon";
    case "oura":
      return "Oura";
    case "whoop":
      return "WHOOP";
    default:
      return "retailer";
  }
}

function ProductPage() {
  const { product } = Route.useLoaderData();
  const logClick = useServerFn(logStoreClick);

  const handleBuy = () => {
    logClick({
      data: { productSlug: product.slug, referrer: window.location.href },
    }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link to="/store" className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to store
          </Link>
          <div className="flex items-center gap-2 font-serif text-lg">
            <ShoppingBag className="h-4 w-4" /> KnowMeno Store
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-4 py-8 md:grid-cols-2">
        <div className="aspect-square w-full overflow-hidden rounded-lg border bg-muted">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.title}
              width={800}
              height={800}
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground/70">
              <ShoppingBag className="h-16 w-16" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {product.brand && (
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {product.brand}
            </span>
          )}
          <h1 className="font-serif text-3xl">{product.title}</h1>
          <p className="text-muted-foreground">{product.short_desc}</p>

          {product.price_display && (
            <div className="text-xl font-semibold">{product.price_display}</div>
          )}

          <a
            href={product.affiliate_url}
            target="_blank"
            rel="nofollow sponsored noopener noreferrer"
            onClick={handleBuy}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-base font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            Buy on {networkLabel(product.affiliate_network)}
            <ExternalLink className="h-4 w-4" />
          </a>

          <AffiliateDisclosure />

          {product.long_desc && (
            <section className="pt-2">
              <h2 className="mb-1 font-semibold">About this product</h2>
              <p className="text-sm text-muted-foreground">{product.long_desc}</p>
            </section>
          )}

          {product.menopause_relevance && (
            <section className="rounded-md bg-muted/50 p-4">
              <h2 className="mb-1 text-sm font-semibold">Why we picked it</h2>
              <p className="text-sm text-muted-foreground">
                {product.menopause_relevance}
              </p>
            </section>
          )}

          <p className="pt-2 text-xs text-muted-foreground">
            KnowMeno does not provide medical advice. Talk to your clinician before
            starting new supplements, devices, or treatments.
          </p>
        </div>
      </main>
    </div>
  );
}
